import { useEffect, useRef, useState } from 'react';
import { isLoadedSnapshot, type DialPacingSnapshot } from './types';

export type DialMilestone = {
	dialsToday: number;
	connectsToday: number;
	demosBookedToday: number;
	dialGoal: number | null;
};

export type UseDialMilestoneTriggerResult = {
	open: boolean;
	milestone: DialMilestone | null;
	close: () => void;
};

/**
 * Watches `snapshot.dialsToday` and opens a milestone dialog every time the
 * count crosses a multiple of `interval`. The first loaded value is treated
 * as the baseline (no fire on mount) so a rep with 12 dials already on the
 * board doesn't see a stale milestone the moment the view loads.
 */
export function useDialMilestoneTrigger(snapshot: DialPacingSnapshot, interval = 5): UseDialMilestoneTriggerResult {
	const lastBucketRef = useRef<number | null>(null);
	const [open, setOpen] = useState(false);
	const [milestone, setMilestone] = useState<DialMilestone | null>(null);

	useEffect(() => {
		if (!isLoadedSnapshot(snapshot)) return;
		const bucket = Math.floor(snapshot.dialsToday / interval);
		if (lastBucketRef.current === null) {
			lastBucketRef.current = bucket;
			return;
		}
		if (bucket > lastBucketRef.current && snapshot.dialsToday > 0) {
			setMilestone({
				dialsToday: snapshot.dialsToday,
				connectsToday: snapshot.connectsToday,
				demosBookedToday: snapshot.demosBookedToday,
				dialGoal: snapshot.dialGoal,
			});
			setOpen(true);
		}
		lastBucketRef.current = bucket;
	}, [snapshot, interval]);

	return {
		open,
		milestone,
		close: () => setOpen(false),
	};
}
